import api from '@common/api';
import { JwtPayload } from '@aws-amplify/core/internals/utils';
import logging from '@common/logging';
import { PropertiesModel } from '@common/models/PropertiesModel';
import { SubscriptionModel } from '@common/models/SubscriptionModel';
import session from '@common/session';
import { AddBankAccountViaPlaidMutationVariables, SFRPropertyInfo } from '@services/appsync/types';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import create from 'zustand';
import { BankAccountsModel } from '../../../../common/models/BankAccountsModel';
import { InvestorAccountModel } from '../../../../common/models/InvestorAccountModel';
import { Investment, SelectedBar } from '../types';
import { fetchAuthSession } from 'aws-amplify/auth';

const BLOCKED_BANK_ACCOUNT_MESSAGE = 'This bank or financial institution is blocked';

const getCityStateFromInfo = (info: SFRPropertyInfo | null) => {
  if (!info) return '';
  const { city, state } = info;
  if (!city || city.length === 0 || !state || state.length === 0) return '';

  return `${city}, ${state}`;
};

interface DashboardModelProps {
  id?: string;
  investments: Investment[] | null;
  selectedBar: SelectedBar;
  user: JwtPayload | null;
  loading: boolean;
}
interface DashboardModelActions {
  addBankAccount: (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => Promise<{ success: boolean; error?: string }>;

  loadUser: () => Promise<void>;
  fetchInvestments: () => Promise<{ error?: string; success: boolean; investments?: Investment[] }>;
  setSelectedBar: (bar: SelectedBar) => void;
  setLoading: (loading: boolean) => void;
}

export const useDashboardModel = create<DashboardModelProps & DashboardModelActions>((set, get) => ({
  addBankAccount: async (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ): Promise<{ error?: string; success: boolean }> => {
    const investorID = session.get()?.investorId;
    if (!investorID) return { error: 'No investor ID', success: false };
    try {
      const accountInfo = metadata.accounts[0];
      const payload: AddBankAccountViaPlaidMutationVariables = {
        bank_account_id: accountInfo.id,
        bank_account_name: accountInfo.name,
        bank_account_type: accountInfo.type,
        investor_account_id: investorID,
        public_token: publicToken,
      };
      await api.addBankAccount(payload);
      await BankAccountsModel.getState().fetchBankAccounts();
      return { success: true };
    } catch (err: any) {
      logging.logError(err);
      if (err.errors[0].message === BLOCKED_BANK_ACCOUNT_MESSAGE) {
        return {
          error:
            'This bank or financial institution is blocked for security reasons. Please link an account from a different institution',
          success: false,
        };
      }
      return { error: 'Unable to add bank account', success: false };
    }
  },
  currentStep: 0,
  dividends: null,
  fetchInvestments: async () => {
    const investorAccount = InvestorAccountModel.getState().investorAccount;
    const subscriptions = SubscriptionModel.getState().subscriptions;
    if (PropertiesModel.getState().properties.length === 0 && investorAccount?.id) {
      await PropertiesModel.getState().fetchProperties(investorAccount.id);
    }
    const properties = PropertiesModel.getState().properties;
    const investments: Investment[] | null = subscriptions.map((subscription) => {
      const property = properties.find((property) => property.id === subscription.subscription_offer_id);

      return property
        ? {
            currentValue: subscription.value?.total_value ?? '0',
            id: subscription.subscription_offer?.slug ?? '',
            income: subscription.tally_dividends ?? '0',
            location: getCityStateFromInfo(property.sfr?.info ?? null),
            name: subscription.subscription_offer?.display_name ?? '',
            photo:
              property.sfr?.info?.images && property.sfr?.info?.images?.length > 0
                ? property.sfr.info.images[0].url
                : '',
          }
        : {
            currentValue: subscription.value?.total_value ?? '0',
            id: null,
            income: subscription.tally_dividends ?? '0',
            location: 'National',
            name: subscription.subscription_offer?.display_name ?? '',
            photo: '/assets/gfx/cash-flow-table-image.png',
          };
    });

    set({ investments });
    return { investments: investments, success: true };
  },
  investments: null,
  loadUser: async () => {
    const amplifySession = await fetchAuthSession();
    set({ user: amplifySession.tokens?.idToken?.payload || null });
  },
  loading: true,
  selectedBar: null,
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  setSelectedBar: (bar: SelectedBar) => {
    set({ selectedBar: bar });
  },
  user: null,
}));
